import React from 'react';
import {
  Box,
  Typography,
  Button,
  Container,
  useTheme,
  keyframes,
  styled
} from '@mui/material';
import { Link } from 'react-router-dom';
import { Home, Search, ArrowBack } from '@mui/icons-material';

// Keyframe animations
const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-20px); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 0.5; }
  50% { opacity: 1; }
`;

const NotFoundPage: React.FC = () => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        minHeight: '80vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: `radial-gradient(circle at 50% 50%, ${theme.palette.primary.main}08 0%, transparent 70%)`,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Background Decoration */}
      <Box
        sx={{
          position: 'absolute',
          top: '20%',
          left: '10%',
          width: '300px',
          height: '300px',
          borderRadius: '50%',
          background: `radial-gradient(circle, ${theme.palette.primary.main}15 0%, transparent 70%)`,
          animation: `${pulse} 4s ease-in-out infinite`,
          filter: 'blur(60px)',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: '20%',
          right: '15%',
          width: '250px',
          height: '250px',
          borderRadius: '50%',
          background: `radial-gradient(circle, ${theme.palette.secondary.main}12 0%, transparent 70%)`,
          animation: `${pulse} 5s ease-in-out infinite`,
          animationDelay: '1s',
          filter: 'blur(60px)',
        }}
      />

      <Container maxWidth="md" sx={{ textAlign: 'center', position: 'relative', zIndex: 2 }}>
        {/* 404 Number */}
        <Box
          sx={{
            mb: 4,
            animation: `${float} 3s ease-in-out infinite`,
          }}
        >
          <Typography
            variant="h1"
            sx={{
              fontSize: { xs: '8rem', md: '12rem' },
              fontWeight: 900,
              background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main}, ${theme.palette.info.main})`,
              backgroundSize: '200% 200%',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              lineHeight: 1,
              letterSpacing: '-0.05em',
            }}
          >
            404
          </Typography>
        </Box>

        {/* Error Message */}
        <Typography
          variant="h3"
          sx={{
            mb: 2,
            fontWeight: 700,
            color: 'text.primary',
            fontSize: { xs: '1.8rem', md: '2.5rem' },
          }}
        >
          Seite nicht gefunden
        </Typography>

        <Typography
          variant="h6"
          color="text.secondary"
          sx={{
            mb: 6,
            maxWidth: 600,
            mx: 'auto',
            lineHeight: 1.7,
            fontSize: { xs: '1rem', md: '1.2rem' },
          }}
        >
          Die gesuchte Seite existiert nicht oder wurde verschoben.
          Keine Sorge – wir helfen Ihnen, den richtigen Weg zu finden.
        </Typography>

        {/* Action Buttons */}
        <Box
          sx={{
            display: 'flex',
            gap: 3,
            justifyContent: 'center',
            flexWrap: 'wrap',
          }}
        >
          <Button
            component={Link}
            to="/"
            variant="contained"
            size="large"
            startIcon={<Home />}
            sx={{
              padding: '16px 32px',
              fontSize: '1.1rem',
              fontWeight: 600,
              borderRadius: '12px',
              background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              boxShadow: `0 8px 32px ${theme.palette.primary.main}40`,
              textTransform: 'none',
              '&:hover': {
                transform: 'translateY(-3px)',
                boxShadow: `0 12px 48px ${theme.palette.primary.main}60`,
              },
            }}
          >
            Zur Startseite
          </Button>

          <Button
            component={Link}
            to="/upload"
            variant="outlined"
            size="large"
            startIcon={<Search />}
            sx={{
              padding: '16px 32px',
              fontSize: '1.1rem',
              fontWeight: 600,
              borderRadius: '12px',
              borderWidth: 2,
              borderColor: theme.palette.primary.main,
              color: theme.palette.primary.main,
              textTransform: 'none',
              '&:hover': {
                borderWidth: 2,
                background: `${theme.palette.primary.main}15`,
                transform: 'translateY(-3px)',
                boxShadow: `0 8px 32px ${theme.palette.primary.main}30`,
              },
            }}
          >
            Analyse starten
          </Button>
        </Box>

        {/* Helpful Links */}
        <Box sx={{ mt: 8 }}>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 2, fontWeight: 500 }}
          >
            Oder besuchen Sie eine dieser Seiten:
          </Typography>
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              justifyContent: 'center',
              flexWrap: 'wrap',
            }}
          >
            <Button
              component={Link}
              to="/vorlagen"
              size="small"
              sx={{
                textTransform: 'none',
                color: 'primary.main',
                '&:hover': {
                  background: `${theme.palette.primary.main}10`,
                },
              }}
            >
              Vorlagen
            </Button>
            <Button
              component={Link}
              to="/feedback"
              size="small"
              sx={{
                textTransform: 'none',
                color: 'primary.main',
                '&:hover': {
                  background: `${theme.palette.primary.main}10`,
                },
              }}
            >
              Feedback
            </Button>
            <Button
              component={Link}
              to="/datenschutz"
              size="small"
              sx={{
                textTransform: 'none',
                color: 'primary.main',
                '&:hover': {
                  background: `${theme.palette.primary.main}10`,
                },
              }}
            >
              Datenschutz
            </Button>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default NotFoundPage;
